'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';

import { customersApi } from '@crm-src/lib/customers-api';
import resellersApiService from '@crm-src/lib/resellers-api';
import { useResellerAccess } from '@crm-src/components/reseller';

/**
 * The generator's ONE recipient picker (CEO, 2026-09-15): "we must be capable
 * to create an invoice for a reseller, just the same way as for a customer",
 * from a list that is "a bit more compact" and finds BOTH.
 *
 * One compact MUI Autocomplete, two groups, in this order:
 *
 *   Customers — `GET /customers?search=…&customer_type=Customer&per_page=20`
 *               (the same light `include_deals=false` payload the form has
 *               always asked for — see docs/crm.md → "Customer dropdown loads
 *               the lightweight payload");
 *   Resellers — `GET /resellers?page=1&per_page=20&search=…` (name OR code,
 *               server-side), rendered "Acme Distribution · 901".
 *
 * Both are filtered by the typed text ON THE SERVER, debounced 250 ms, so a
 * tenant with thousands of customers is searched, not scrolled. Each option is
 * ONE line — the name, with the customer number / reseller code as a small
 * caption — which is the "more compact" half of the ask.
 *
 * The reseller wall: behind `useResellerAccess()` the Resellers group is simply
 * absent AND the resellers endpoint is never called. The picker never renders
 * a `.reseller.name`; the reseller row is flattened into `label`/`caption` at
 * the API boundary (`resellerOption`), so the wall has one gate here, not one
 * per render. Pinned by customer-or-reseller-picker.test.tsx and the
 * reseller-wall safety net (reseller-wall.test.ts).
 *
 * What a pick DOES lives in the form (`generate-invoice-form.tsx`): a customer
 * runs the ordinary customer flow; a reseller puts the form into the very same
 * state `?resellerId=` does (customer-less document addressed to the reseller,
 * `<code>-0000-NNN`). This component only answers "who".
 */

export type RecipientOption =
  | {
      kind: 'customer';
      id: string;
      /** The company name — what the input shows once picked. */
      label: string;
      /** The customer number (`700-0007`), or the email when there is none. */
      caption: string;
      customer: any;
    }
  | {
      kind: 'reseller';
      id: string;
      label: string;
      /** The three-digit reseller code — the same segment that leads the
       * document number. */
      caption: string;
      reseller: any;
    };

/** How long after the last keystroke both endpoints are asked. */
export const RECIPIENT_SEARCH_DEBOUNCE_MS = 250;
/** How many of each the list offers per search — a picker, not a report. */
export const RECIPIENT_SEARCH_LIMIT = 20;

export const RECIPIENT_GROUP_LABELS: Record<RecipientOption['kind'], string> = {
  customer: 'Customers',
  reseller: 'Resellers',
};

/** The label the input shows and the validation copy refers to. */
export const RECIPIENT_PICKER_LABEL = 'Customer or reseller';

export function customerOption(customer: any): RecipientOption {
  const label =
    customer?.company_name ||
    customer?.full_name ||
    customer?.name ||
    `${customer?.first_name || ''} ${customer?.last_name || ''}`.trim() ||
    'Unnamed';
  return {
    kind: 'customer',
    id: String(customer?.id ?? ''),
    label,
    caption: customer?.customer_id || customer?.email || '',
    customer,
  };
}

export function resellerOption(reseller: any): RecipientOption {
  return {
    kind: 'reseller',
    id: String(reseller?.id ?? ''),
    label: reseller?.legal_name || reseller?.name || 'Unnamed',
    caption: reseller?.code || '',
    reseller,
  };
}

function sameOption(a: RecipientOption | null, b: RecipientOption | null): boolean {
  return !!a && !!b && a.kind === b.kind && a.id === b.id;
}

export interface CustomerOrResellerPickerProps {
  value: RecipientOption | null;
  onChange: (option: RecipientOption | null) => void;
  /** The form's validation message for this field, if any. */
  error?: string;
  disabled?: boolean;
}

export const CustomerOrResellerPicker: React.FC<CustomerOrResellerPickerProps> = ({
  value,
  onChange,
  error,
  disabled = false,
}) => {
  const resellerAccess = useResellerAccess();
  const [query, setQuery] = useState('');
  const [customers, setCustomers] = useState<RecipientOption[]>([]);
  const [resellers, setResellers] = useState<RecipientOption[]>([]);
  const [searching, setSearching] = useState(false);

  // Debounced search of BOTH lists. Runs once on mount with an empty term so
  // the list opens populated, then on every settled keystroke. The reseller
  // half is skipped entirely — not fetched-then-hidden — behind the wall.
  useEffect(() => {
    let cancelled = false;
    const handle = setTimeout(async () => {
      setSearching(true);
      const term = query.trim();
      try {
        const [customerPage, resellerPage] = await Promise.all([
          customersApi
            .getAll({
              ...(term ? { search: term } : {}),
              customer_type: 'Customer',
              per_page: RECIPIENT_SEARCH_LIMIT,
              include_deals: false,
            })
            .catch(() => ({ customers: [] as any[] })),
          resellerAccess
            ? resellersApiService
                .getPage({ page: 1, per_page: RECIPIENT_SEARCH_LIMIT, ...(term ? { search: term } : {}) })
                .catch(() => null)
            : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setCustomers((customerPage?.customers ?? []).map(customerOption));
        setResellers((resellerPage?.data ?? []).map(resellerOption));
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, RECIPIENT_SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query, resellerAccess]);

  // The picked entry must be IN the list for MUI to show it as selected, even
  // when the current search no longer returns it (or it was set by a deep link
  // / a reopened document). It is slotted into ITS group so the two groups stay
  // contiguous — MUI renders a duplicate group header otherwise.
  const options = useMemo(() => {
    const withValue = (group: RecipientOption[], kind: RecipientOption['kind']) =>
      value && value.kind === kind && !group.some((o) => sameOption(o, value)) ? [value, ...group] : group;
    return [...withValue(customers, 'customer'), ...withValue(resellerAccess ? resellers : [], 'reseller')];
  }, [customers, resellers, value, resellerAccess]);

  return (
    <Box data-cy="customer-or-reseller-picker" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Autocomplete<RecipientOption, false, false, false>
        size="small"
        options={options}
        value={value}
        disabled={disabled}
        loading={searching}
        onChange={(_event, next) => {
          onChange(next);
          // Search everyone again, so reopening the list offers a DIFFERENT
          // recipient rather than only the one just picked.
          setQuery('');
        }}
        onInputChange={(_event, next, reason) => {
          // "reset" is MUI writing the picked name into the box; treating it as
          // a search would narrow the list to that one entry.
          if (reason === 'reset') return;
          setQuery(next);
        }}
        groupBy={(option) => RECIPIENT_GROUP_LABELS[option.kind]}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={sameOption}
        // Both halves are filtered server-side; MUI must not re-filter what
        // came back (the caption is not part of the label).
        filterOptions={(all) => all}
        noOptionsText={query.trim() ? 'No customer or reseller matches.' : 'Type a name to search.'}
        renderOption={(props, option) => {
          const { key: _key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key?: string };
          return (
            <Box
              component="li"
              key={`${option.kind}-${option.id}`}
              {...rest}
              data-cy={`recipient-option-${option.kind}`}
              sx={{
                '&.MuiAutocomplete-option': { minHeight: 32, py: 0.5, gap: 1, alignItems: 'baseline' },
              }}
            >
              <Typography component="span" noWrap sx={{ fontWeight: 500 }}>
                {option.label}
              </Typography>
              {option.caption && (
                <Typography component="span" variant="caption" noWrap sx={{ color: 'text.secondary' }}>
                  · {option.caption}
                </Typography>
              )}
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={RECIPIENT_PICKER_LABEL}
            required
            placeholder="Type a customer or reseller name"
            error={!!error}
            inputProps={{ ...params.inputProps, 'data-cy': 'customer-or-reseller-input' }}
          />
        )}
      />

      {error && (
        <Typography
          component="p"
          data-cy="recipient-error"
          sx={{ fontSize: 14, color: 'error.main', display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          <AlertTriangle size={16} />
          {error}
        </Typography>
      )}
    </Box>
  );
};
